import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createInviteSplit, createSplit } from '../../../api/splits';
import { uri } from '../../../constants';
import axios from 'axios';
import emails from '../../../arrays/emails.json';

const SplitModal = ({
  email,
  isrc,
  title,
  allUsers,
  uid,
  onClose,
  currentItems
}) => {
  // States
  const [splits, setSplits] = useState([]);
  const [newSplits, setNewSplits] = useState([]);
  const [collaborator, setCollaborator] = useState('');
  const [percentage, setPercentage] = useState(0);
  const [inviteMode, setInviteMode] = useState(false);
  const [error, setError] = useState('');
  const [recoupEmail, setRecoupEmail] = useState('');
  const [recoup, setRecoup] = useState(0);
  const [saveText, setSaveText] = useState('Save');
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [pendingInviteEmail, setPendingInviteEmail] = useState('');
  const [pendingInvitePercentage, setPendingInvitePercentage] = useState(0);

  // Refs
  const successBtn = useRef();
  const modalRef = useRef();
  const queryClient = useQueryClient();

  // Set up modal event listeners
  useEffect(() => {
    // Load data immediately when component mounts or isrc/currentItems change
    if (isrc && currentItems && currentItems.length > 0) {
      loadExistingSplitData();
    }
    
    const modalElement = document.getElementById('addShareModal');
    
    if (modalElement) {
      // Define the event listener as a named function for proper cleanup
      const handleModalShow = () => {
        loadExistingSplitData();
      };
      
      // Attach the event listener
      modalElement.addEventListener('show.bs.modal', handleModalShow);
      
      // Cleanup event listener on unmount
      return () => {
        modalElement.removeEventListener('show.bs.modal', handleModalShow);
      };
    }
  }, [isrc, currentItems]);
  
  // Helper function to load existing split data
  const loadExistingSplitData = () => {
    if (isrc && currentItems) {
      console.log("Searching for splits with ISRC:", isrc);
      console.log("currentItems:", currentItems);
      
      // Try to find splits with matching ISRC
      const existingSplits = currentItems.filter(item => {
        // Check different possible structures
        const itemIsrc = item.isrc || (item.track && item.track.isrc);
        return itemIsrc === isrc;
      });
      
      if (existingSplits.length > 0) {
        console.log("Found split data:", existingSplits);
        
        // Map the data to match our splits structure
        const mappedSplits = existingSplits.map(split => {
          // Extract the ISRC from either direct property or track object
          const splitIsrc = split.isrc || (split.track && split.track.isrc);
          
          return {
            id: split.id, // Preserve the ID for updating
            user_id: split.user_id || null,
            user: split.email,
            recoupment: parseFloat(split.recoupment) || 0,
            percentage: parseFloat(split.share) || 0,
            track: splitIsrc,
            title: split.title,
            isInvited: !split.user_id
          };
        });
        
        setSplits(mappedSplits);
      } else {
        console.log("No splits found for ISRC:", isrc);
        // Clear splits if none found
        setSplits([]);
      }
    }
  };

  // Error handling
  const handleError = (errorMsg) => {
    setError(errorMsg);
    setTimeout(() => {
      setError('');
    }, 3000);
  };

  // Calculate total shares
  const calculateTotalShares = () => {
    let total = 0;
    if (splits?.length > 0) {
      splits.forEach(split => {
        if (split.track === isrc) {
          total += parseFloat(split.percentage) || 0;
        }
      });
    }
    return total;
  };

  // Handle collaborator input change
  const handleCollaboratorInputChange = (e) => {
    const inputValue = e.target.value;
    setCollaborator(inputValue);
    
    // Check if this is a valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmailFormat = emailRegex.test(inputValue);
    
    if (isValidEmailFormat) {
      // Check if email exists in our system
      const exists = allUsers?.some(user => 
        user.email?.toLowerCase() === inputValue.toLowerCase()
      );
      setInviteMode(!exists);
    } else {
      setInviteMode(false);
    }
  };

  // Add collaborator function
  const addCollaborator = () => {
    if (collaborator.trim() === '') {
      handleError('Please enter collaborator email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(collaborator)) {
      handleError('Please enter a valid email address');
      return;
    }

    if (percentage <= 0) {
      handleError('Percentage must be greater than 0');
      return;
    }

    const totalShares = calculateTotalShares();
    if (totalShares + parseFloat(percentage) > 100) {
      handleError('Shares sum cannot exceed 100');
      return;
    }

    // Check if user is already added
    if (splits?.some(split => 
      split.track === isrc && 
      split.user?.toLowerCase() === collaborator.toLowerCase()
    )) {
      handleError('This collaborator is already added');
      return;
    }

    // Check if user exists in the system
    const userExists = allUsers?.some(user => 
      user.email?.toLowerCase() === collaborator.toLowerCase()
    );

    if (!userExists) {
      setPendingInviteEmail(collaborator);
      setPendingInvitePercentage(percentage);
      const inviteModal = new bootstrap.Modal(document.getElementById('inviteConfirmModal'));
      inviteModal.show();
    } else {
      const userID = allUsers?.find(user => 
        user.email?.toLowerCase() === collaborator.toLowerCase()
      )?.user_id;
      
      addSplitToState(userID, collaborator, percentage);
    }
  };

  // Helper function to add split to state
  const addSplitToState = (userID, email, sharePercentage) => {
    // Check if this email already exists in the splits
    const existingSplit = splits.find(split => 
      split.track === isrc && split.user?.toLowerCase() === email.toLowerCase()
    );

    if (existingSplit) {
      // Update existing split
      const updatedSplits = splits.map(split => {
        if (split.track === isrc && split.user?.toLowerCase() === email.toLowerCase()) {
          return {
            ...split,
            percentage: sharePercentage,
            user_id: userID || split.user_id
          };
        }
        return split;
      });
      
      setSplits(updatedSplits);
      setNewSplits([...newSplits, {
        id: existingSplit.id, // Preserve ID if it exists
        user_id: userID || existingSplit.user_id,
        user: email,
        recoupment: existingSplit.recoupment || 0,
        percentage: sharePercentage,
        track: isrc,
        title: title,
        isInvited: !userID
      }]);
    } else {
      // Add new split
      const newSplit = {
        user_id: userID || null,
        user: email,
        recoupment: 0,
        percentage: sharePercentage,
        track: isrc,
        title: title,
        isInvited: !userID
      };
      
      setSplits([...splits, newSplit]);
      setNewSplits([...newSplits, newSplit]);
    }
    
    setCollaborator('');
    setPercentage(0);
  };

  // Send email function

  // Confirm adding non-existing user
  const confirmAddInvitedUser = async () => {
    try {
      addSplitToState(null, pendingInviteEmail, pendingInvitePercentage);

      
      const modal = bootstrap.Modal.getInstance(document.getElementById('inviteConfirmModal'));
      modal.hide();
      
      setPendingInviteEmail('');
      setPendingInvitePercentage(0);
    } catch (error) {
      console.error('Error sending invite:', error);
      handleError('Failed to send invite. Please try again.');
    }
  };

  // Add recoupment function
  const addRecoup = () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recoupEmail)) {
      handleError('Please enter a valid email address for recoupment');
      return;
    }

    if (recoup <= 0) {
      handleError('Recoupment amount must be greater than 0');
      return;
    }

    // Check if this email already has recoupment
    const existingRecoupment = splits.find(split => 
      split.track === isrc && 
      split.user.toLowerCase() === recoupEmail.toLowerCase() &&
      split.recoupment > 0
    );

    if (existingRecoupment) {
      handleError('This email already has recoupment set');
      return;
    }

    // Check if this email exists in splits
    const splitIndex = splits.findIndex(split => 
      split.track === isrc && 
      split.user.toLowerCase() === recoupEmail.toLowerCase()
    );

    if (splitIndex === -1) {
      // Email not found in splits, add it with 0% share but with recoupment
      const newSplit = {
        user_id: null,
        user: recoupEmail,
        recoupment: recoup,
        percentage: 0,
        track: isrc,
        title: title,
        isRecoupmentOnly: true
      };
      
      setSplits([...splits, newSplit]);
      setNewSplits([...newSplits, newSplit]);
    } else {
      // Update the recoupment for this email
      const updatedSplits = [...splits];
      updatedSplits[splitIndex].recoupment = recoup;
      setSplits(updatedSplits);
      
      // Update in newSplits as well if it exists there
      setNewSplits(prevSplits => 
        prevSplits.map(item => 
          item.user.toLowerCase() === recoupEmail.toLowerCase() && item.track === isrc 
            ? { ...item, recoupment: recoup } 
            : item
        )
      );
    }
    
    // Reset inputs
    setRecoup(0);
    setRecoupEmail('');
  };

  // Save shares function
  const saveShares = async () => {
    setSaveText('Please Wait...');
    setBtnDisabled(true);
    try {
      // For splits that were loaded from currentItems, we need to update rather than create
      const allSplits = splits.map(spl => ({
        id: spl.id, // Will be undefined for new splits
        user_id: spl.user_id,
        email: spl.user,
        recoupment: parseFloat(spl.recoupment) || 0,
        share: parseFloat(spl.percentage) || 0,
        isrc: spl.track,
        title: spl.title,
        isInvited: spl.isInvited || false,
        isRecoupmentOnly: spl.isRecoupmentOnly || false
      }));
      
      await createSplit(allSplits, isrc);
      queryClient.invalidateQueries(["all_splits", uid]);
      console.log("Splits saved successfully");
      setNewSplits([]);
      setSaveText('Save');
      setBtnDisabled(false);
      // Show success modal
      successBtn.current.click();
      handleClose();
      
      // Reset state
     
    } catch (error) {
      console.error('An error occurred:', error.message);
      setSaveText("An error occurred");
      setBtnDisabled(false);
    }
  };

  // Close modal handler
  const handleClose = () => {
    const modal = bootstrap.Modal.getInstance(document.getElementById('addShareModal'));
    const successModal = bootstrap.Modal.getInstance(document.getElementById("successModal"));
    if (modal) {
      modal.hide();
    }
    if (successModal) {
      successModal.hide();
    }
    
    // Reset all state completely
    setSplits([]);
    setNewSplits([]);
    setCollaborator('');
    setPercentage(0);
    setRecoupEmail('');
    setRecoup(0);
    setError('');
    setSaveText('Save');
    setBtnDisabled(false);
    setInviteMode(false);
    setPendingInviteEmail('');
    setPendingInvitePercentage(0);
    
    // Call parent close handler if provided
    if (onClose) onClose();
  };

  return (
    <>
      <div className="modal fade" id="addShareModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="addShareModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="shareModalLabel">Add Share</h1>
              <button className='success-close-btn text-dark' onClick={handleClose} type="button">
                <i className="fi fi-rr-cross"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table dash-table">
                  <thead>
                    <tr>
                      <td>Collaborator</td>
                      <td className='text-center'>Percentage</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="table-item">{email}</div>
                      </td>
                      <td>
                        <div className="table-item text-center">
                          {100 - calculateTotalShares()}%
                        </div>
                      </td>
                      <td></td>
                    </tr>
                    {splits.map((share, i) => (
                      share.track === isrc && (
                        <tr key={i}>
                          <td>
                            <input
                              type="text"
                              placeholder='Email'
                              value={share.user}
                              onChange={(e) => {
                                const updatedSplits = [...splits];
                                updatedSplits[i].user = e.target.value;
                                setSplits(updatedSplits);
                                setNewSplits(prevSplits => prevSplits.map(item => 
                                  item.percentage === share.percentage ? { ...item, user: e.target.value } : item
                                ))
                              }}
                              className='dash-form-input'
                            />
                          </td>
                          <td>
                            <div className="table-item text-center">
                              <input
                                type="number"
                                placeholder='0'
                                value={share.percentage}
                                min={0}
                                onChange={(e) => {
                                  if (calculateTotalShares() >= 100) {
                                    handleError('Shares sum cannot exceed 100')
                                    const updatedSplits = [...splits];
                                    updatedSplits[i].percentage = 0;
                                    setSplits(updatedSplits);
                                  } else if (e.target.value * 1 < 0) {
                                    handleError('Please enter a valid value')
                                  } else {
                                    const updatedSplits = [...splits];
                                    updatedSplits[i].percentage = e.target.value;
                                    setSplits(updatedSplits);
                                    setNewSplits(prevSplits => prevSplits.map(item => 
                                      item.user === share.user ? { ...item, percentage: e.target.value } : item
                                    ))
                                  }
                                }}
                                className='dash-form-input share-input me-1'
                              />
                              %
                            </div>
                          </td>
                          <td>
                            <div className="table-item d-flex justify-content-center mt-2">
                              <i
                                className="fi fi-rr-trash table-action-btn a-red"
                                onClick={() => {
                                  const spiltUser = share.user
                                  const updatedSplits = [...splits]
                                  updatedSplits.splice(i, 1)
                                  setSplits(updatedSplits)
                                  setNewSplits(prevSplits => prevSplits.filter(item => item.user !== spiltUser))
                                }}
                              ></i>
                            </div>
                          </td>
                        </tr>
                      )
                    ))}
                    <tr>
                      <td>
                        <div className="position-relative">
                          <input 
                            type="text" 
                            placeholder='Email' 
                            value={collaborator} 
                            onChange={handleCollaboratorInputChange} 
                            className={`dash-form-input ${inviteMode ? 'border-warning' : ''}`}
                          />
                          {inviteMode && collaborator && (
                            <small className="text-warning d-block mt-1">
                              <i className="fi fi-rr-info-circle me-1"></i>
                              User not in system - will be invited
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="table-item text-center">
                          <input 
                            type="number" 
                            min={0} 
                            placeholder='0' 
                            value={percentage} 
                            onChange={e => setPercentage(e.target.value)} 
                            className='dash-form-input share-input me-1' 
                          />
                          %
                        </div>
                      </td>
                      <td>
                        <div className="table-item d-flex justify-content-center">
                          <button 
                            className="dash-card-heading-btn share-btn" 
                            onClick={addCollaborator}
                          >
                            Add
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {error !== '' && <h6 className='text-danger text-center fw-bold mb-2'>{error}</h6>}
              <h6 className="mt-3 fw-bold">
                Are there any collaborators that require recoupment before sharing royalies?
                <br />
                Add below
              </h6>
              <div className="table-responsive mt-3 pb-5">
                <table className="table dash-table">
                  <thead>
                    <tr>
                      <td>Recoupment</td>
                      <td className='text-center'>Amount</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody>
                    {splits.map((share, i) => 
                      share.track === isrc && share.recoupment * 1 > 0 && (
                        <tr key={i}>
                          <td>
                            <div className="table-item">{share.user}</div>
                          </td>
                          <td>
                            <div className="table-item text-center">{share.recoupment}</div>
                          </td>
                          <td>
                            <div className="table-item d-flex justify-content-center">
                              <i
                                className="fi fi-rr-trash table-action-btn a-red"
                                onClick={() => {
                                  const updatedSplits = [...splits];
                                  updatedSplits[i].recoupment = 0;
                                  setSplits(updatedSplits);
                                }}
                              ></i>
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                    {splits.find(item => item.track === isrc && item.recoupment * 1 > 0) === undefined && (
                      <tr>
                        <td>
                          <input
                            type="email"
                            placeholder="Email for Recoupment"
                            value={recoupEmail}
                            onChange={e => setRecoupEmail(e.target.value)}
                            className="dash-form-input"
                          />
                        </td>
                        <td>
                          <div className="table-item text-center">
                            $
                            <input 
                              type="number" 
                              min={0} 
                              placeholder='0' 
                              value={recoup} 
                              onChange={e => setRecoup(e.target.value)} 
                              className='dash-form-input share-input ms-1' 
                            />
                          </div>
                        </td>
                        <td>
                          <div className="table-item d-flex justify-content-center">
                            <button 
                              className={`dash-card-heading-btn share-btn ${!recoupEmail || recoup <= 0 ? 'disabled' : ''}`} 
                              disabled={!recoupEmail || recoup <= 0} 
                              onClick={addRecoup}
                            >
                              Add
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-center">
                <button className="dash-card-heading-btn cancel-btn share-btn me-2" data-bs-dismiss="modal">Cancel</button>
                <button className="dash-card-heading-btn share-btn" data-bs-toggle="modal" data-bs-target="#confirmModal">Save</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <div className="modal fade" id="confirmModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="confirmModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body success-modal">
              <button className='success-close-btn' data-bs-dismiss="modal" aria-label="Close">
                <i className="fi fi-rr-cross"></i>
              </button>
              <h5>Are you sure you want to make these changes?</h5>
              <div className="d-flex justify-content-center mt-3">
                <button 
                  className="dash-card-heading-btn cancel-btn share-btn me-2" 
                  data-bs-toggle="modal" 
                  data-bs-target="#addShareModal"
                >
                  Cancel
                </button>
                <button 
                  className={`dash-card-heading-btn share-btn ${btnDisabled && 'disabled'}`} 
                  disabled={btnDisabled} 
                  onClick={saveShares}
                >
                  {saveText}
                </button>
                <button 
                  ref={successBtn} 
                  data-bs-toggle="modal" 
                  data-bs-target="#successModal" 
                  hidden
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <div className="modal fade" id="successModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body success-modal">
              <button 
                className='success-close-btn' 
                onClick={handleClose}
                aria-label="Close"
              >
                <i className="fi fi-rr-cross"></i>
              </button>
              <i className="fi fi-rr-check-circle text-success success-icon"></i>
              <h5>Splits Updated Successfully!</h5>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Confirmation Modal */}
      <div className="modal fade" id="inviteConfirmModal" tabIndex="-1" data-bs-backdrop="static" aria-labelledby="inviteConfirmModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <button className='success-close-btn' data-bs-dismiss="modal" aria-label="Close">
                <i className="fi fi-rr-cross"></i>
              </button>
              <div className="text-center mb-3">
                <i className="fi fi-rr-info-circle text-warning" style={{ fontSize: '48px' }}></i>
              </div>
              <h5 className="text-center mb-3">User Not Found</h5>
              <p className="text-center">
                <strong>{pendingInviteEmail}</strong> is not registered in our system. 
                We'll send them an invitation to join the platform and claim their share.
              </p>
              <div className="d-flex justify-content-center mt-4">
                <button 
                  className="dash-card-heading-btn cancel-btn share-btn me-2" 
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button 
                  className="dash-card-heading-btn share-btn" 
                  onClick={confirmAddInvitedUser}
                >
                  Send Invite & Add
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SplitModal; 